import React, { useRef } from "react";

interface CodeDigitInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  onKeyDown?: (index: number, e: React.KeyboardEvent) => void;
  onPaste?: (e: React.ClipboardEvent) => void;
  hasError?: boolean;
  disabled?: boolean;
  length?: number;
  className?: string;
  acceptText?: boolean;
}

/**
 * Componente de entrada de código com múltiplos campos
 * Permite inserir códigos de verificação com navegação automática entre campos
 * 
 * @param props - Propriedades do componente
 * @returns {JSX.Element} Componente React de entrada de código
 */
const CodeDigitInput: React.FC<CodeDigitInputProps> = ({
  values,
  onChange,
  onKeyDown,
  onPaste,
  hasError = false,
  disabled = false,
  length = 6,
  className = "",
  acceptText = false
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  /**
   * Manipula a mudança de valor em um campo específico
   * Valida a entrada baseada no tipo aceito e move para o próximo campo automaticamente
   * 
   * @param {number} index - Índice do campo que foi alterado
   * @param {string} value - Novo valor inserido
   */
  const handleInputChange = (index: number, value: string) => {
    // Validar entrada baseado no tipo aceito
    if (acceptText) {
      // Aceita qualquer caractere alfanumérico e converte para maiúsculo
      if (!/^[a-zA-Z0-9]*$/.test(value)) return;
      value = value.toUpperCase();
    } else {
      // Aceita apenas números
      if (!/^\d*$/.test(value)) return;
    }

    const newValues = [...values];
    newValues[index] = value;
    onChange(newValues);

    // Mover para o próximo campo se um dígito foi inserido
    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  /**
   * Manipula eventos de teclado para navegação entre campos
   * Move para o campo anterior quando Backspace é pressionado em campo vazio
   * 
   * @param {number} index - Índice do campo atual
   * @param {React.KeyboardEvent} e - Evento de teclado
   */
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Voltar para o campo anterior se Backspace foi pressionado e o campo está vazio
    if (e.key === "Backspace" && !values[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    
    if (onKeyDown) {
      onKeyDown(index, e);
    }
  };

  /**
   * Manipula eventos de colar texto
   * Filtra e distribui o texto colado pelos campos disponíveis
   * 
   * @param {React.ClipboardEvent} e - Evento de colar
   */
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    let pastedData = e.clipboardData.getData("text").slice(0, length);
    
    // Filtrar dados colados baseado no tipo aceito
    if (acceptText) {
      pastedData = pastedData.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    } else {
      pastedData = pastedData.replace(/\D/g, "");
    }
    
    const newValues = new Array(length).fill("");
    
    for (let i = 0; i < pastedData.length; i++) {
      newValues[i] = pastedData[i];
    }
    
    onChange(newValues);
    
    // Focar no último campo preenchido ou no próximo vazio
    const nextEmptyIndex = newValues.findIndex(digit => !digit);
    const focusIndex = nextEmptyIndex === -1 ? length - 1 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
    
    if (onPaste) {
      onPaste(e);
    }
  };

  /** Classes CSS base para os campos de entrada */
  const baseClass = "w-10 h-10 md:w-14 md:h-12 text-center text-base md:text-lg font-semibold border rounded-lg focus:outline-none focus:ring-2";
  
  /** Classes CSS baseadas no estado (erro ou normal) */
  const stateClass = hasError
    ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
    : "border-input focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <div className={`flex justify-start gap-2 md:gap-3 ${className}`}>
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(el) => (inputRefs.current[index] = el)}
          type="text"
          inputMode={acceptText ? "text" : "numeric"}
          maxLength={1}
          value={values[index] || ""}
          onChange={(e) => handleInputChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          className={`${baseClass} ${stateClass}`}
          disabled={disabled}
        />
      ))}
    </div>
  );
};

export default CodeDigitInput;
